import React, { useState, useEffect } from 'react';
import { API_BASE } from '../config';
import {
  Sliders,
  Code2,
  Sparkles,
  Trash2,
  AlertCircle,
  Plus,
  X
} from 'lucide-react';
import {
  PII_TYPE_OPTIONS,
  MASK_STRATEGY_OPTIONS,
  SAMPLE_PAYMENT_PAYLOAD,
  convertFieldsToJson,
  parseJsonToFields,
  validateVaultizeSchema,
  inferSchemaClientSide
} from './schemaValidationUtils';

const EditSchemaModal = ({ schema, apiKey, tenantUid, token, onClose, onUpdated }) => {
  const [description, setDescription] = useState(schema.description || '');
  const [editorMode, setEditorMode] = useState('visual'); // 'visual' | 'json' | 'ai_convert'
  const [fields, setFields] = useState([]);
  const [schemaJson, setSchemaJson] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationStatus, setValidationStatus] = useState(null);

  // AI Sample Payload-to-Schema Converter State
  const [samplePayloadText, setSamplePayloadText] = useState(SAMPLE_PAYMENT_PAYLOAD);
  const [filterMode, setFilterMode] = useState('sensitive_only');
  const [pathStyle, setPathStyle] = useState('leaf_name');
  const [aiConverting, setAiConverting] = useState(false);

  const getHeaders = () => {
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (apiKey) headers['X-API-KEY'] = apiKey;
    if (tenantUid) headers['X-TENANT-ID'] = tenantUid;
    return headers;
  };

  useEffect(() => {
    const loadLatestVersion = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await fetch(`${API_BASE}/admin/schemas/${schema.id}/versions`, {
          headers: getHeaders()
        });
        if (!res.ok) throw new Error('Failed to load schema versions');
        const page = await res.json();

        if (page.content && page.content.length > 0) {
          const parsedObj = JSON.parse(page.content[0].definition);
          const formatted = JSON.stringify(parsedObj, null, 2);
          setSchemaJson(formatted);
          setValidationStatus(validateVaultizeSchema(formatted));

          const parsedFields = parseJsonToFields(formatted);
          if (parsedFields && parsedFields.length > 0) {
            setFields(parsedFields);
            setEditorMode('visual');
          } else {
            setEditorMode('json');
          }
        }
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    loadLatestVersion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema.id, apiKey, tenantUid, token]);

  const handleSwitchMode = (newMode) => {
    if (newMode === editorMode) return;

    if (newMode === 'visual') {
      const check = validateVaultizeSchema(schemaJson);
      if (!check.valid) {
        setValidationStatus(check);
        setError(check.errors[0] || 'Fix Schema JSON errors before switching to Visual Builder.');
        return;
      }
      const parsedFields = parseJsonToFields(schemaJson);
      if (!parsedFields) {
        setError('Cannot switch to Visual Builder: missing valid "fields" object.');
        return;
      }
      setFields(parsedFields);
      setError('');
    } else if (newMode === 'json') {
      if (editorMode === 'visual' && fields.length > 0) {
        const synced = convertFieldsToJson(fields);
        setSchemaJson(synced);
        setValidationStatus(validateVaultizeSchema(synced));
      } else {
        setValidationStatus(validateVaultizeSchema(schemaJson));
      }
      setError('');
    } else {
      setError('');
    }
    setEditorMode(newMode);
  };

  const handleJsonChange = (val) => {
    setSchemaJson(val);
    setError('');
    setValidationStatus(validateVaultizeSchema(val));
  };

  const addField = () => {
    const newField = {
      name: `field_${fields.length + 1}`,
      piiType: 'generic',
      required: true,
      formatPreserving: false,
      preserveLuhn: false,
      maskStrategy: 'TOKENIZE',
      regex: '',
      description: ''
    };
    const updated = [...fields, newField];
    setFields(updated);
    setSchemaJson(convertFieldsToJson(updated));
  };

  const updateField = (index, fieldUpdate) => {
    const updated = [...fields];
    updated[index] = { ...updated[index], ...fieldUpdate };
    if (fieldUpdate.piiType === 'card') {
      updated[index].formatPreserving = true;
      updated[index].preserveLuhn = true;
    } else if (fieldUpdate.piiType && fieldUpdate.piiType !== 'card') {
      updated[index].preserveLuhn = false;
    }
    setFields(updated);
    setSchemaJson(convertFieldsToJson(updated));
  };

  const removeField = (index) => {
    if (fields.length <= 1) {
      setError('A schema must contain at least one field definition.');
      return;
    }
    const updated = fields.filter((_, i) => i !== index);
    setFields(updated);
    setSchemaJson(convertFieldsToJson(updated));
  };

  const convertSamplePayloadToSchema = async (overridePayloadString) => {
    const rawText = overridePayloadString !== undefined ? overridePayloadString : samplePayloadText;
    setError('');
    let parsedSample;
    try {
      parsedSample = JSON.parse(rawText);
    } catch (e) {
      setError(`Sample payload is not valid JSON: ${e.message}`);
      return;
    }

    setAiConverting(true);
    try {
      let conversionResult = null;
      try {
        const res = await fetch(`${API_BASE}/admin/schemas/generate-from-json`, {
          method: 'POST',
          headers: getHeaders(),
          body: JSON.stringify({
            sampleJson: parsedSample,
            filterMode,
            pathStyle,
            aiProvider: 'auto'
          })
        });
        if (res.ok) {
          conversionResult = await res.json();
        }
      } catch {
        // Fallback to local inference
      }

      if (!conversionResult || !conversionResult.schemaJson) {
        conversionResult = inferSchemaClientSide(parsedSample, filterMode, pathStyle);
      }

      const formattedSchema = JSON.stringify(conversionResult.schemaJson, null, 2);
      setSchemaJson(formattedSchema);
      const parsedFields = parseJsonToFields(formattedSchema);
      if (parsedFields && parsedFields.length > 0) {
        setFields(parsedFields);
      }
      setValidationStatus(validateVaultizeSchema(formattedSchema));
      setEditorMode('visual');
      setError('');
    } catch (e) {
      setError(e.message || 'Failed to convert sample payload');
    } finally {
      setAiConverting(false);
    }
  };

  const saveChanges = async () => {
    setError('');

    let jsonToSave = schemaJson;
    if (editorMode === 'visual' && fields.length > 0) {
      jsonToSave = convertFieldsToJson(fields);
      setSchemaJson(jsonToSave);
    }

    const validation = validateVaultizeSchema(jsonToSave);
    setValidationStatus(validation);
    if (!validation.valid) {
      setError(validation.errors.join(' '));
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/schemas/${schema.id}/versions`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({ schemaJson: jsonToSave })
      });
      if (!res.ok) {
        let errMessage = 'Failed to update schema version';
        try {
          const errJson = await res.json();
          errMessage = errJson.message || errJson.error || JSON.stringify(errJson);
        } catch {
          const text = await res.text();
          if (text) errMessage = text;
        }
        throw new Error(errMessage);
      }
      await res.json();
      onUpdated();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content modal-lg">
        <h3 className="modal-title">
          <span>Edit Schema: <em>{schema.name}</em></span>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label="Close"
            disabled={loading}
          >
            <X size={20} />
          </button>
        </h3>
        <p className="modal-subtitle">
          Create a new immutable version of this schema with full validation.
        </p>

        {error && (
          <div className="schema-error">
            <div className="schema-error-msg-row">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
            {validationStatus?.isRawPayload && (
              <div className="raw-payload-recovery-bar">
                <span>Detected a raw JSON payload instead of a schema definition.</span>
                <button
                  type="button"
                  className="btn-convert-payload-inline"
                  onClick={() => convertSamplePayloadToSchema(schemaJson)}
                >
                  <Sparkles size={14} style={{ marginRight: 6 }} />
                  Auto-Convert This Payload into a Vaultize Schema
                </button>
              </div>
            )}
          </div>
        )}

        <div className="form-group">
          <label className="form-label">Description</label>
          <input
            className="form-input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={loading}
          />
        </div>

        <div className="schema-mode-tabs-wrapper">
          <div className="schema-mode-tabs">
            <button
              type="button"
              className={`schema-tab-btn ${editorMode === 'visual' ? 'active' : ''}`}
              onClick={() => handleSwitchMode('visual')}
            >
              <Sliders size={15} />
              <span>Visual Field Builder</span>
              <span className="tab-field-count">{fields.length}</span>
            </button>
            <button
              type="button"
              className={`schema-tab-btn ${editorMode === 'json' ? 'active' : ''}`}
              onClick={() => handleSwitchMode('json')}
            >
              <Code2 size={15} />
              <span>Schema JSON Editor</span>
            </button>
            <button
              type="button"
              className={`schema-tab-btn ai-tab-highlight ${editorMode === 'ai_convert' ? 'active' : ''}`}
              onClick={() => handleSwitchMode('ai_convert')}
            >
              <Sparkles size={15} />
              <span>AI Convert Sample JSON</span>
            </button>
          </div>
        </div>

        {editorMode === 'visual' && (
          <div className="visual-field-builder">
            <div className="builder-header-row">
              <div>
                <h4 className="builder-title">Schema Fields ({fields.length})</h4>
              </div>
              <button
                type="button"
                className="btn btn-secondary btn-sm add-field-btn"
                onClick={addField}
              >
                <Plus size={14} /> Add Field
              </button>
            </div>

            <div className="fields-list">
              {fields.map((field, idx) => {
                const selectedPii = PII_TYPE_OPTIONS.find((opt) => opt.value === field.piiType);
                const showLuhn = selectedPii?.supportsLuhn || field.piiType === 'card';
                return (
                  <div key={idx} className="field-card">
                    <div className="field-card-main-row">
                      <div className="field-card-input-group">
                        <label className="field-micro-label">Field Name</label>
                        <input
                          type="text"
                          className="form-input field-input-name"
                          value={field.name}
                          onChange={(e) => updateField(idx, { name: e.target.value })}
                        />
                      </div>

                      <div className="field-card-input-group">
                        <label className="field-micro-label">PII Type</label>
                        <select
                          className="form-input field-select"
                          value={field.piiType}
                          onChange={(e) => updateField(idx, { piiType: e.target.value })}
                        >
                          {PII_TYPE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>

                      <div className="field-card-input-group">
                        <label className="field-micro-label">Mask Strategy</label>
                        <select
                          className="form-input field-select"
                          value={field.maskStrategy}
                          onChange={(e) => updateField(idx, { maskStrategy: e.target.value })}
                        >
                          {MASK_STRATEGY_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>

                      <button
                        type="button"
                        className="btn-field-delete"
                        onClick={() => removeField(idx)}
                        aria-label="Remove Field"
                        disabled={fields.length <= 1}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>

                    <div className="field-card-toggles-row">
                      <label className="toggle-label">
                        <input
                          type="checkbox"
                          checked={field.required}
                          onChange={(e) => updateField(idx, { required: e.target.checked })}
                        />
                        <span>Required</span>
                      </label>
                      <label className="toggle-label">
                        <input
                          type="checkbox"
                          checked={field.formatPreserving}
                          onChange={(e) => updateField(idx, { formatPreserving: e.target.checked })}
                        />
                        <span>Format-Preserving (FPE)</span>
                      </label>
                      {showLuhn && (
                        <label className="toggle-label highlight-luhn">
                          <input
                            type="checkbox"
                            checked={field.preserveLuhn}
                            onChange={(e) => updateField(idx, { preserveLuhn: e.target.checked })}
                          />
                          <span>Preserve Luhn</span>
                        </label>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {editorMode === 'json' && (
          <div className="form-group">
            <div className="label-with-badge">
              <label className="form-label">Schema JSON</label>
              {validationStatus && (
                <span className={`schema-validation-pill ${validationStatus.valid ? 'valid' : 'invalid'}`}>
                  {validationStatus.valid
                    ? `Valid Schema (${validationStatus.fieldCount} fields)`
                    : 'Invalid Schema Format'}
                </span>
              )}
            </div>
            <textarea
              className="schema-textarea enhanced-textarea"
              rows="12"
              value={schemaJson}
              onChange={(e) => handleJsonChange(e.target.value)}
              spellCheck={false}
              disabled={loading}
            />
          </div>
        )}

        {editorMode === 'ai_convert' && (
          <div className="ai-schema-converter-panel">
            <div className="ai-converter-options-grid">
              <div className="form-group">
                <label className="field-micro-label">Field Selection Filter</label>
                <select
                  className="form-input field-select"
                  value={filterMode}
                  onChange={(e) => setFilterMode(e.target.value)}
                >
                  <option value="sensitive_only">Sensitive PII / PCI Fields Only</option>
                  <option value="all_fields">Include All Leaf Fields</option>
                </select>
              </div>
              <div className="form-group">
                <label className="field-micro-label">Nested Key Style</label>
                <select
                  className="form-input field-select"
                  value={pathStyle}
                  onChange={(e) => setPathStyle(e.target.value)}
                >
                  <option value="leaf_name">Concise Field Names (accountNumber)</option>
                  <option value="dot_path">Full Dot-Path (paymentInformation.card.accountNumber)</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Paste Sample JSON Payload to Convert</label>
              <textarea
                className="schema-textarea enhanced-textarea"
                rows="8"
                value={samplePayloadText}
                onChange={(e) => setSamplePayloadText(e.target.value)}
                spellCheck={false}
              />
            </div>
            <button
              type="button"
              className="btn btn-primary"
              disabled={aiConverting}
              onClick={() => convertSamplePayloadToSchema()}
            >
              <Sparkles size={15} style={{ marginRight: 6 }} />
              {aiConverting ? 'Converting...' : 'Convert Payload & Load into Builder'}
            </button>
          </div>
        )}

        <div className="modal-actions">
          <button className="btn btn-primary" disabled={loading} onClick={saveChanges}>
            {loading ? 'Validating & Saving...' : 'Save New Version'}
          </button>
          <button className="btn btn-secondary" onClick={onClose} disabled={loading}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditSchemaModal;
